/**
 * One overlay per round (spec 2026-09-15-round-result-overlay-design):
 *
 *   result → REVEAL (5.4 s) → SETTLED   when the settlement is already in hand
 *                           → SETTLING  otherwise, capped at 20 s
 *   no bet  → REVEAL → idle + "result" banner
 *   dismiss / cap / newer round → idle; anything later for that round is a banner.
 *
 * Pure: the page feeds it events with `now`, renders from `state`, and shows
 * whatever `banner` comes back. Nothing here reads a clock or the DOM.
 */
export const REVEAL_MS = 5400;
export const SETTLING_CAP_MS = 20_000;
/** A dismissed round's settlement still opens the card if it lands within this long of the result (PM, 2026-09-15). */
export const REOPEN_WINDOW_MS = 15_000;
const CLOSED_ROUNDS_KEPT = 20;

export type OverlayStage = 'idle' | 'reveal' | 'settling' | 'settled';
export type NetByCurrency = Record<string, number>;
export type OverlaySettlement = { round: number; netByCurrency: NetByCurrency };

export type OverlayState = {
  stage: OverlayStage;
  /** The round the overlay is about; keeps the last value after closing so older results are ignored. */
  round: number;
  hasBets: boolean;
  revealEndsAt: number;
  settlingSince: number;
  settledAt: number;
  /** When the current round's result went public — the reopen window counts from here. */
  resultAt: number;
  /** A round the player dismissed before its settlement was shown; its settlement may still open the card. */
  awaitingRound: number;
  /** Settlement received for `round`; may arrive during REVEAL and wait there. */
  settlement: OverlaySettlement | null;
  /** Rounds whose overlay is finished. Never reopened. */
  closedRounds: number[];
  /** Rounds whose settled figures reached the player (card or banner), with those figures. */
  delivered: Record<number, NetByCurrency>;
};

export type OverlayEvent =
  | { type: 'result'; round: number; hasBets: boolean; now: number }
  | { type: 'settlement'; settlement: OverlaySettlement; now: number }
  | { type: 'tick'; now: number }
  | { type: 'dismiss'; now: number }
  | { type: 'reset' };

export type OverlayBanner =
  | { kind: 'result'; round: number }
  | { kind: 'settling'; round: number }
  | { kind: 'credited'; round: number; netByCurrency: NetByCurrency }
  | { kind: 'corrected'; round: number; netByCurrency: NetByCurrency };

type Step = { state: OverlayState; banner: OverlayBanner | null };

export function initialOverlayState(): OverlayState {
  return {
    stage: 'idle',
    round: 0,
    hasBets: false,
    revealEndsAt: 0,
    settlingSince: 0,
    settledAt: 0,
    resultAt: 0,
    awaitingRound: 0,
    settlement: null,
    closedRounds: [],
    delivered: {},
  };
}

export function sameNets(a: NetByCurrency | undefined, b: NetByCurrency): boolean {
  const keys = new Set([...Object.keys(a || {}), ...Object.keys(b)]);
  for (const key of keys) {
    if (Math.abs((a?.[key] || 0) - (b[key] || 0)) > 0.005) return false;
  }
  return true;
}

function closeRound(state: OverlayState, round: number): OverlayState {
  const closedRounds = [...state.closedRounds.filter((item) => item !== round), round].slice(-CLOSED_ROUNDS_KEPT);
  return { ...state, stage: 'idle', hasBets: false, revealEndsAt: 0, settlingSince: 0, settledAt: 0, settlement: null, closedRounds };
}

function deliver(state: OverlayState, round: number, netByCurrency: NetByCurrency): OverlayState {
  return { ...state, delivered: { ...state.delivered, [round]: netByCurrency } };
}

export function reduceOverlay(state: OverlayState, event: OverlayEvent): Step {
  switch (event.type) {
    case 'reset':
      return { state: initialOverlayState(), banner: null };

    case 'result': {
      if (event.round <= state.round || state.closedRounds.includes(event.round)) return { state, banner: null };
      let next = state;
      let banner: OverlayBanner | null = null;
      if (state.stage !== 'idle') {
        // A newer round's result closes whatever is open; the old round's
        // pending settlement follows the banner rule.
        if (state.stage === 'settling') banner = { kind: 'settling', round: state.round };
        if (state.stage === 'reveal' && state.settlement) {
          next = deliver(next, state.round, state.settlement.netByCurrency);
          banner = { kind: 'credited', round: state.round, netByCurrency: state.settlement.netByCurrency };
        }
        next = closeRound(next, state.round);
      }
      next = {
        ...next,
        stage: 'reveal',
        round: event.round,
        hasBets: event.hasBets,
        revealEndsAt: event.now + REVEAL_MS,
        settlingSince: 0,
        settledAt: 0,
        resultAt: event.now,
        awaitingRound: 0,
        settlement: null,
      };
      return { state: next, banner };
    }

    case 'settlement': {
      const { round, netByCurrency } = event.settlement;
      if (state.stage !== 'idle' && state.round === round) {
        if (state.stage === 'reveal') return { state: { ...state, settlement: event.settlement }, banner: null };
        if (state.stage === 'settling') {
          return { state: deliver({ ...state, stage: 'settled', settledAt: event.now, settlement: event.settlement }, round, netByCurrency), banner: null };
        }
        // SETTLED: a re-settle while the card is still up just refreshes the card.
        return { state: deliver({ ...state, settlement: event.settlement }, round, netByCurrency), banner: null };
      }
      if (state.stage === 'idle' && round === state.awaitingRound && event.now - state.resultAt <= REOPEN_WINDOW_MS) {
        // Dismissed the animation, but the money landed quickly: the card still pops.
        return { state: deliver({ ...state, stage: 'settled', settledAt: event.now, settlement: event.settlement, awaitingRound: 0 }, round, netByCurrency), banner: null };
      }
      const shown = state.delivered[round];
      if (shown) {
        if (sameNets(shown, netByCurrency)) return { state, banner: null };
        return { state: deliver(state, round, netByCurrency), banner: { kind: 'corrected', round, netByCurrency } };
      }
      return { state: deliver(state, round, netByCurrency), banner: { kind: 'credited', round, netByCurrency } };
    }

    case 'tick': {
      if (state.stage === 'reveal' && event.now >= state.revealEndsAt) {
        if (!state.hasBets) return { state: closeRound(state, state.round), banner: { kind: 'result', round: state.round } };
        if (state.settlement) {
          return { state: deliver({ ...state, stage: 'settled', settledAt: event.now }, state.round, state.settlement.netByCurrency), banner: null };
        }
        return { state: { ...state, stage: 'settling', settlingSince: event.now }, banner: null };
      }
      if (state.stage === 'settling' && event.now - state.settlingSince >= SETTLING_CAP_MS) {
        return { state: closeRound(state, state.round), banner: { kind: 'settling', round: state.round } };
      }
      return { state, banner: null };
    }

    case 'dismiss': {
      if (state.stage === 'idle') return { state, banner: null };
      if (state.stage === 'reveal' && state.settlement) {
        // The animation was skipped but the settlement is already here: show the card.
        return { state: deliver({ ...state, stage: 'settled', settledAt: event.now }, state.round, state.settlement.netByCurrency), banner: null };
      }
      if (state.stage === 'settled') return { state: { ...closeRound(state, state.round), awaitingRound: 0 }, banner: null };
      // Reveal or settling without a settlement yet: closed, but a settlement
      // inside the reopen window still opens the card.
      return { state: { ...closeRound(state, state.round), awaitingRound: state.round }, banner: null };
    }
  }
}
